import { FormBuilderContext } from '@montugroup/form-builder';
import { useContext } from 'react';
import { createPath, Navigate, parsePath, useLocation } from 'react-router-dom';

export type RedirectWithSearchProps = {
  to: string;
  /**
   * Replace the current history entry instead of pushing a new one. Use for
   * entry-point paths a patient is sent to from outside the app, where a
   * pushed entry would redirect forward again and trap the back button.
   */
  replace?: boolean;
};

/**
 * Redirects while preserving the query string and fragment, which carry the
 * patient's details between forms. A query or fragment on `to` is kept too,
 * and wins where it names the same param.
 */
export const RedirectWithSearch = ({ to, replace = false }: RedirectWithSearchProps) => {
  const { search, hash } = useLocation();
  const formBuilder = useContext(FormBuilderContext);
  const target = parsePath(to);

  // Only an explicit false waits: outside a form there is nothing to wait for,
  // and redirecting early would carry the patient past a failure.
  if (formBuilder?.hasSaved === false) {
    return null;
  }

  const params = new URLSearchParams(search);
  new URLSearchParams(target.search).forEach((value, key) => params.set(key, value));
  const mergedSearch = params.toString();

  return (
    <Navigate
      to={createPath({
        pathname: target.pathname,
        search: mergedSearch ? `?${mergedSearch}` : '',
        hash: target.hash || hash,
      })}
      replace={replace}
    />
  );
};

export default RedirectWithSearch;
